import React, { useEffect, useState, useRef } from "react";
import { http, currency, API } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Loader2, Plus, Trash2, Pencil, X, Upload, GripVertical, ArrowUp, ArrowDown, Star } from "lucide-react";
import { toast } from "sonner";

const EMPTY_PRODUCT = {
  name: "",
  description: "",
  category: "",
  base_price: 0,
  delivery_days: 7,
  base_image_url: "",
  mockup_image_url: "",
  images: [],
  colors: [],
  sizes: [],
  has_custom_measure: false,
  mockup: { top: 30, left: 30, width: 40, height: 40, blend_mode: "multiply" },
  bulk_rules: [],
  printing_methods: [],
  design_service: { enabled: false, price: 99, description: "Diseño gráfico profesional creado por nuestro equipo." },
  extra_services: [],
  active: true,
  sort_order: 0,
};

// Auto-slug for extra services from their name (stable id)
const slugify = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || `svc-${Math.random().toString(36).slice(2, 8)}`;

const PRINTING_PRESETS = [
  { code: "dtf", label: "DTF", description: "Impresión textil directa a film" },
  { code: "dtf_uv", label: "DTF UV", description: "DTF UV para superficies rígidas" },
  { code: "sublimacion", label: "Sublimación", description: "Ideal para poliéster o cerámica" },
  { code: "bordado", label: "Bordado", description: "Bordado con hilo poliéster" },
  { code: "serigrafia", label: "Serigrafía", description: "Tirajes grandes, colores planos" },
  { code: "vinil_uv", label: "Vinil UV", description: "Impresión UV con relieve" },
  { code: "grabado", label: "Grabado láser", description: "Sin color, permanente" },
];

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([http.get("/admin/products"), http.get("/admin/categories")])
      .then(([p, c]) => {
        setItems(p.data);
        setCategories(c.data.filter((x) => x.active));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const open = (p) => {
    if (p) {
      setEditing(p);
      setForm({
        ...EMPTY_PRODUCT,
        ...p,
        bulk_rules: p.bulk_rules || [],
        printing_methods: p.printing_methods || [],
        design_service: p.design_service || EMPTY_PRODUCT.design_service,
        extra_services: (p.extra_services || []).map((s) => ({ ...s })),
        images: p.images && p.images.length ? p.images : (p.base_image_url ? [p.base_image_url] : []),
      });
    } else {
      setEditing({});
      setForm({ ...EMPTY_PRODUCT, category: categories[0]?.slug || "" });
    }
  };
  const close = () => setEditing(null);

  const save = async () => {
    if (!form.name || !form.category) {
      toast.error("Nombre y categoría son requeridos");
      return;
    }
    if (!form.images?.length && !form.base_image_url) {
      toast.error("Sube al menos una imagen del producto");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        base_image_url: form.images?.[0] ? (form.images[0].startsWith("http") ? form.images[0] : `${API}/files/${form.images[0]}`) : form.base_image_url,
        images: form.images || [],
        mockup_image_url: form.mockup_image_url || (form.images?.[0] || form.base_image_url || ""),
        base_price: Number(form.base_price),
        delivery_days: Number(form.delivery_days),
        min_qty: Math.max(1, parseInt(form.min_qty || 1, 10)),
        colors: form.colors.map((c) => ({ ...c, extra_price: Number(c.extra_price || 0) })),
        sizes: form.sizes.map((s) => ({ ...s, extra_price: Number(s.extra_price || 0) })),
        bulk_rules: form.bulk_rules.map((r) => ({ min_qty: Number(r.min_qty), price_off: Number(r.price_off) })),
        printing_methods: form.printing_methods.map((m) => ({
          code: m.code,
          label: m.label,
          description: m.description || null,
          extra_price: Number(m.extra_price || 0),
        })),
        design_service: {
          enabled: !!form.design_service?.enabled,
          price: Number(form.design_service?.price || 0),
          description: form.design_service?.description || "",
        },
        extra_services: (form.extra_services || [])
          .filter((s) => (s.name || "").trim().length > 0)
          .map((s, idx) => ({
            id: s.id || slugify(s.name) || `svc-${idx}`,
            name: (s.name || "").trim(),
            description: s.description || "",
            price: Number(s.price || 0),
            enabled: s.enabled !== false,
          })),
        sort_order: Number(form.sort_order || 0),
      };
      if (editing?.product_id) {
        await http.put(`/admin/products/${editing.product_id}`, payload);
        toast.success("Producto actualizado");
      } else {
        await http.post("/admin/products", payload);
        toast.success("Producto creado");
      }
      close();
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`¿Eliminar "${p.name}"?`)) return;
    try {
      await http.delete(`/admin/products/${p.product_id}`);
      toast.success("Producto eliminado");
      load();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  // Reorder products in the shop by moving one row up/down (bumps sort_order).
  const moveProduct = async (row, dir) => {
    const newOrder = Math.max(0, (row.sort_order || 0) + dir);
    if (newOrder === (row.sort_order || 0)) return;
    try {
      await http.put(`/admin/products/${row.product_id}/sort`, { sort_order: newOrder });
      load();
    } catch {
      toast.error("No se pudo reordenar");
    }
  };

  const setTopPriority = async (row) => {
    // Give it the lowest sort_order among the current list minus 1
    const minOrder = items.reduce((min, p) => Math.min(min, Number(p.sort_order || 0)), 0);
    const newOrder = minOrder - 1;
    try {
      await http.put(`/admin/products/${row.product_id}/sort`, { sort_order: newOrder });
      toast.success(`"${row.name}" ahora es el primero en /tienda`);
      load();
    } catch {
      toast.error("No se pudo priorizar");
    }
  };

  // Variant helpers
  const addColor = () => setForm((f) => ({ ...f, colors: [...f.colors, { label: "", value: "", hex: "#000000", extra_price: 0 }] }));
  const updColor = (i, k, v) => setForm((f) => ({ ...f, colors: f.colors.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const rmColor = (i) => setForm((f) => ({ ...f, colors: f.colors.filter((_, idx) => idx !== i) }));
  const addSize = () => setForm((f) => ({ ...f, sizes: [...f.sizes, { label: "", value: "", extra_price: 0 }] }));
  const updSize = (i, k, v) => setForm((f) => ({ ...f, sizes: f.sizes.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const rmSize = (i) => setForm((f) => ({ ...f, sizes: f.sizes.filter((_, idx) => idx !== i) }));
  const addRule = () => setForm((f) => ({ ...f, bulk_rules: [...f.bulk_rules, { min_qty: 10, price_off: 5 }] }));
  const updRule = (i, k, v) => setForm((f) => ({ ...f, bulk_rules: f.bulk_rules.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const rmRule = (i) => setForm((f) => ({ ...f, bulk_rules: f.bulk_rules.filter((_, idx) => idx !== i) }));

  const addMethod = (preset) => {
    setForm((f) => {
      if (preset && f.printing_methods.some((m) => m.code === preset.code)) return f;
      return {
        ...f,
        printing_methods: [
          ...f.printing_methods,
          preset
            ? { ...preset, extra_price: 0 }
            : { code: "", label: "", description: "", extra_price: 0 },
        ],
      };
    });
  };
  const updMethod = (i, k, v) => setForm((f) => ({ ...f, printing_methods: f.printing_methods.map((c, idx) => (idx === i ? { ...c, [k]: v } : c)) }));
  const rmMethod = (i) => setForm((f) => ({ ...f, printing_methods: f.printing_methods.filter((_, idx) => idx !== i) }));

  const imageInputRef = useRef(null);
  const [uploadingImages, setUploadingImages] = useState(false);
  const uploadImages = async (files) => {
    if (!files || !files.length) return;
    setUploadingImages(true);
    try {
      const uploaded = [];
      for (const file of Array.from(files)) {
        if (!/\.(png|jpe?g|webp|gif)$/i.test(file.name)) continue;
        if (file.size > 15 * 1024 * 1024) continue;
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await http.post("/admin/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.storage_path);
      }
      setForm((f) => ({ ...f, images: [...(f.images || []), ...uploaded] }));
      toast.success(`${uploaded.length} imagen(es) subida(s)`);
    } catch (e) {
      toast.error("Error al subir imágenes");
    } finally {
      setUploadingImages(false);
    }
  };

  const removeImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));
  const moveImage = (i, dir) => setForm((f) => {
    const arr = [...(f.images || [])];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return f;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    return { ...f, images: arr };
  });
  const setPrimaryImage = (i) => setForm((f) => {
    const arr = [...(f.images || [])];
    if (i <= 0 || i >= arr.length) return f;
    const [picked] = arr.splice(i, 1);
    arr.unshift(picked);
    return { ...f, images: arr };
  });
  const imageSrc = (p) => (p.startsWith("http") ? p : `${API}/files/${p}`);

  return (
    <div className="space-y-6" data-testid="admin-products-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase text-neutral-500">/admin/productos</div>
          <h2 className="font-display text-3xl md:text-4xl tracking-tighter mt-1">Productos</h2>
          <p className="text-xs text-neutral-500 mt-2 max-w-xl">
            El orden de esta tabla es el mismo que ve el cliente en <span className="font-mono">/tienda</span>. Usa la <Star className="w-3 h-3 inline text-amber-600" /> para enviar un producto al primer lugar, o las flechas para ajustarlo uno a uno. La columna &ldquo;Orden&rdquo; muestra el valor actual (menor = primero).
          </p>
        </div>
        <Button onClick={() => open(null)} className="brutal-btn bg-black hover:bg-[#FF3B30] text-white h-10" data-testid="admin-add-product">
          <Plus className="w-4 h-4 mr-2" /> Nuevo producto
        </Button>
      </div>

      <div className="brutal-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-neutral-400" /></div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-neutral-500">Sin productos. Crea el primero.</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-200 text-left">
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Orden</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Producto</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Categoría</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Precio base</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Mín.</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Reglas mayoreo</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Métodos</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500">Estado</th>
                <th className="p-3 text-xs font-mono uppercase text-neutral-500"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((p, idx) => (
                <tr key={p.product_id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`admin-prod-row-${p.product_id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setTopPriority(p)}
                        className={`p-1 border ${idx === 0 ? "border-amber-500 bg-amber-50 text-amber-600" : "border-neutral-200 hover:bg-amber-50 hover:border-amber-400 hover:text-amber-600 text-neutral-400"}`}
                        title={idx === 0 ? "Ya está en primer lugar" : "Enviar al primer lugar de /tienda"}
                        data-testid={`admin-prod-top-${p.product_id}`}
                      >
                        <Star className={`w-3 h-3 ${idx === 0 ? "fill-amber-500" : ""}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveProduct(p, -1)}
                        className="p-1 border border-neutral-200 hover:bg-neutral-100 disabled:opacity-30"
                        disabled={(p.sort_order || 0) === 0}
                        title="Subir prioridad"
                        data-testid={`admin-prod-up-${p.product_id}`}
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <span className="text-[11px] font-mono px-1.5 min-w-[26px] text-center text-neutral-600" data-testid={`admin-prod-sort-${p.product_id}`}>
                        {p.sort_order || 0}
                      </span>
                      <button
                        type="button"
                        onClick={() => moveProduct(p, 1)}
                        className="p-1 border border-neutral-200 hover:bg-neutral-100"
                        title="Bajar prioridad"
                        data-testid={`admin-prod-down-${p.product_id}`}
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-neutral-100 border border-neutral-200 overflow-hidden flex-shrink-0">
                        {p.base_image_url && <img src={p.base_image_url} alt="" className="w-full h-full object-cover" />}
                      </div>
                      <div>
                        <div className="font-medium">{p.name}</div>
                        <div className="text-xs text-neutral-500 truncate max-w-xs">{p.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-xs font-mono">{p.category}</td>
                  <td className="p-3 font-medium">{currency(p.base_price)}</td>
                  <td className="p-3 text-xs font-mono" data-testid={`admin-prod-min-${p.product_id}`}>
                    {p.min_qty && p.min_qty > 1 ? (
                      <span className="px-1.5 py-0.5 border border-[#FF3B30] text-[#FF3B30]">≥ {p.min_qty}</span>
                    ) : (
                      <span className="text-neutral-400">1</span>
                    )}
                  </td>
                  <td className="p-3 text-xs">
                    {p.bulk_rules?.length > 0 ? `${p.bulk_rules.length} regla(s)` : <span className="text-neutral-400">Sin reglas</span>}
                  </td>
                  <td className="p-3 text-xs">
                    {p.printing_methods?.length > 0 ? (
                      <span className="font-mono">{p.printing_methods.map((m) => m.label).join(" · ")}</span>
                    ) : (
                      <span className="text-neutral-400">—</span>
                    )}
                  </td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 border ${p.active ? "border-emerald-500 text-emerald-700" : "border-neutral-300 text-neutral-500"}`}>
                      {p.active ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => open(p)} data-testid={`admin-edit-prod-${p.product_id}`}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-none h-8 border-red-400 text-red-600 hover:bg-red-50" onClick={() => remove(p)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 flex" data-testid="admin-prod-drawer">
          <div className="flex-1 bg-black/40" onClick={close} />
          <div className="w-full max-w-2xl bg-white h-full overflow-auto border-l border-neutral-200">
            <div className="sticky top-0 bg-white border-b border-neutral-200 p-4 flex items-center justify-between z-10">
              <h3 className="font-display text-xl tracking-tight">{editing.product_id ? "Editar" : "Nuevo"} producto</h3>
              <button onClick={close} className="p-2 hover:bg-neutral-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-mono uppercase">Nombre</Label>
                  <Input className="rounded-none mt-2" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prod-form-name" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Categoría</Label>
                  <select
                    className="w-full mt-2 border border-neutral-200 rounded-none px-3 h-10 bg-white text-sm"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="prod-form-category"
                  >
                    <option value="">Selecciona...</option>
                    {categories.map((c) => (<option key={c.slug} value={c.slug}>{c.name}</option>))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Descripción</Label>
                  <Textarea className="rounded-none mt-2" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="prod-form-desc" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Imágenes del producto ({(form.images || []).length}/8)</Label>
                  <div className="mt-2 grid grid-cols-4 gap-2">
                    {(form.images || []).map((img, i) => {
                      const isMockup = form.mockup_image_url === img;
                      return (
                        <div key={i} className={`relative aspect-square border-2 bg-neutral-50 group ${isMockup ? "border-purple-500" : "border-neutral-200"}`} data-testid={`prod-image-${i}`}>
                          <img src={imageSrc(img)} alt={`img-${i}`} className="w-full h-full object-cover" />
                          {i === 0 && (
                            <div className="absolute top-1 left-1 text-[9px] font-mono uppercase bg-black text-white px-1.5 py-0.5">Principal</div>
                          )}
                          {isMockup && (
                            <div className="absolute top-1 right-1 text-[9px] font-mono uppercase bg-purple-600 text-white px-1.5 py-0.5" data-testid={`prod-image-mockup-badge-${i}`}>
                              Diseño
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100">
                            {i !== 0 && (
                              <button
                                type="button"
                                onClick={() => setPrimaryImage(i)}
                                className="p-1 bg-white hover:bg-amber-100 text-amber-600"
                                title="Establecer como imagen principal"
                                data-testid={`prod-image-set-primary-${i}`}
                              >
                                <Star className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => setForm({ ...form, mockup_image_url: img })}
                              className={`p-1 hover:bg-purple-100 ${isMockup ? "bg-purple-500 text-white" : "bg-white text-purple-700"}`}
                              title={isMockup ? "Ya es la imagen de diseño" : "Marcar como imagen de diseño"}
                              data-testid={`prod-image-set-mockup-${i}`}
                            >
                              <span className="text-[9px] font-mono uppercase px-1">Diseño</span>
                            </button>
                            <button type="button" onClick={() => moveImage(i, -1)} className="p-1 bg-white hover:bg-neutral-200"><ArrowUp className="w-3 h-3" /></button>
                            <button type="button" onClick={() => moveImage(i, 1)} className="p-1 bg-white hover:bg-neutral-200"><ArrowDown className="w-3 h-3" /></button>
                            <button type="button" onClick={() => removeImage(i)} className="p-1 bg-white hover:bg-red-100 text-red-600" data-testid={`prod-image-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
                          </div>
                        </div>
                      );
                    })}
                    {(form.images || []).length < 8 && (
                      <button
                        type="button"
                        onClick={() => imageInputRef.current?.click()}
                        className="aspect-square border-2 border-dashed border-neutral-300 hover:border-black flex flex-col items-center justify-center gap-1 text-neutral-500 hover:text-black transition-colors"
                        data-testid="prod-image-upload-btn"
                      >
                        {uploadingImages ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                        <span className="text-[10px] font-mono uppercase">Subir</span>
                      </button>
                    )}
                  </div>
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={(e) => uploadImages(e.target.files)}
                    data-testid="prod-image-input"
                  />
                  <p className="text-xs text-neutral-500 mt-2">Sube hasta 8 imágenes (PNG, JPG, WEBP · max 15 MB). La primera es la principal — la que verá el cliente en la tienda. Pasa el cursor sobre una miniatura y toca <Star className="w-3 h-3 inline text-amber-600" /> para promoverla a principal. <strong className="text-purple-700">Marca UNA como &quot;Diseño&quot;</strong> — será la imagen sobre la que el cliente coloca su arte (mockup editable).</p>
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Precio base (MXN)</Label>
                  <Input type="number" step="0.01" className="rounded-none mt-2" value={form.base_price} onChange={(e) => setForm({ ...form, base_price: e.target.value })} data-testid="prod-form-price" />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase">Días de entrega</Label>
                  <Input type="number" className="rounded-none mt-2" value={form.delivery_days} onChange={(e) => setForm({ ...form, delivery_days: e.target.value })} data-testid="prod-form-delivery" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs font-mono uppercase">Pedido mínimo (piezas)</Label>
                  <div className="flex items-center gap-3 mt-2">
                    <Input
                      type="number"
                      min="1"
                      step="1"
                      className="rounded-none w-32"
                      value={form.min_qty ?? 1}
                      onChange={(e) => setForm({ ...form, min_qty: e.target.value })}
                      data-testid="prod-form-min-qty"
                    />
                    <p className="text-xs text-neutral-500 leading-snug flex-1">
                      Cantidad mínima que un cliente puede comprar. Ej: lanyard sencillo → 6 piezas. Usa 1 si no aplica.
                    </p>
                  </div>
                </div>
              </div>

              {/* Colors */}
              <div className="border border-neutral-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-sm">Colores</div>
                  <Button size="sm" variant="outline" className="rounded-none h-8" onClick={addColor} data-testid="prod-form-add-color"><Plus className="w-3 h-3 mr-1" /> Agregar</Button>
                </div>
                <div className="space-y-2">
                  {form.colors.map((c, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <Input placeholder="Etiqueta" className="col-span-4 rounded-none h-9 text-sm" value={c.label} onChange={(e) => { updColor(i, "label", e.target.value); if (!c.value) updColor(i, "value", e.target.value.toLowerCase().replace(/\s+/g,"_")); }} />
                      <Input placeholder="valor" className="col-span-3 rounded-none h-9 text-sm font-mono" value={c.value} onChange={(e) => updColor(i, "value", e.target.value)} />
                      <input type="color" className="col-span-2 h-9 w-full border border-neutral-200" value={c.hex || "#000000"} onChange={(e) => updColor(i, "hex", e.target.value)} />
                      <Input type="number" placeholder="+$" className="col-span-2 rounded-none h-9 text-sm" value={c.extra_price} onChange={(e) => updColor(i, "extra_price", e.target.value)} />
                      <button onClick={() => rmColor(i)} className="p-2 hover:bg-neutral-100"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Sizes */}
              <div className="border border-neutral-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-sm">Tallas / Tamaños</div>
                  <Button size="sm" variant="outline" className="rounded-none h-8" onClick={addSize} data-testid="prod-form-add-size"><Plus className="w-3 h-3 mr-1" /> Agregar</Button>
                </div>
                <div className="space-y-2">
                  {form.sizes.map((s, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <Input placeholder="Etiqueta" className="col-span-5 rounded-none h-9 text-sm" value={s.label} onChange={(e) => { updSize(i, "label", e.target.value); if (!s.value) updSize(i, "value", e.target.value.toLowerCase().replace(/\s+/g,"_")); }} />
                      <Input placeholder="valor" className="col-span-4 rounded-none h-9 text-sm font-mono" value={s.value} onChange={(e) => updSize(i, "value", e.target.value)} />
                      <Input type="number" placeholder="+$" className="col-span-2 rounded-none h-9 text-sm" value={s.extra_price} onChange={(e) => updSize(i, "extra_price", e.target.value)} />
                      <button onClick={() => rmSize(i)} className="p-2 hover:bg-neutral-100"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
                <label className="flex items-center gap-2 text-xs mt-3">
                  <input type="checkbox" checked={form.has_custom_measure} onChange={(e) => setForm({ ...form, has_custom_measure: e.target.checked })} />
                  Permitir medidas personalizadas (ancho × alto en cm)
                </label>
              </div>

              {/* Bulk rules */}
              <div className="border-2 border-[#FF3B30] p-4 bg-red-50/40">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium text-sm">Reglas de mayoreo</div>
                    <div className="text-xs text-neutral-500">Descuento por unidad al alcanzar cierta cantidad.</div>
                  </div>
                  <Button size="sm" variant="outline" className="rounded-none h-8 border-[#FF3B30] text-[#FF3B30]" onClick={addRule} data-testid="prod-form-add-rule"><Plus className="w-3 h-3 mr-1" /> Regla</Button>
                </div>
                <div className="space-y-2 mt-3">
                  {form.bulk_rules.length === 0 && <div className="text-xs text-neutral-500 italic">Sin reglas. Ej: 30 piezas → -$10 por unidad.</div>}
                  {form.bulk_rules.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center bg-white p-2 border border-neutral-200">
                      <div className="col-span-1 text-xs text-neutral-500 text-center">A partir de</div>
                      <Input type="number" min="2" className="col-span-3 rounded-none h-9 text-sm" value={r.min_qty} onChange={(e) => updRule(i, "min_qty", e.target.value)} data-testid={`prod-rule-qty-${i}`} />
                      <div className="col-span-2 text-xs text-neutral-500 text-center">piezas → descuento</div>
                      <div className="col-span-1 text-sm text-right">$</div>
                      <Input type="number" step="0.5" min="0" className="col-span-3 rounded-none h-9 text-sm" value={r.price_off} onChange={(e) => updRule(i, "price_off", e.target.value)} data-testid={`prod-rule-off-${i}`} />
                      <div className="col-span-1 text-xs text-neutral-500 text-center">/ unidad</div>
                      <button onClick={() => rmRule(i)} className="p-2 hover:bg-neutral-100"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Printing methods */}
              <div className="border border-neutral-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium text-sm">Métodos de impresión</div>
                    <div className="text-xs text-neutral-500">Define con qué técnicas se puede personalizar este producto.</div>
                  </div>
                  <div className="flex gap-1">
                    <select
                      className="border border-neutral-200 rounded-none px-2 h-8 bg-white text-xs"
                      onChange={(e) => {
                        const preset = PRINTING_PRESETS.find((p) => p.code === e.target.value);
                        if (preset) addMethod(preset);
                        e.target.value = "";
                      }}
                      defaultValue=""
                      data-testid="prod-form-add-method-preset"
                    >
                      <option value="" disabled>+ Preset...</option>
                      {PRINTING_PRESETS.map((p) => (
                        <option key={p.code} value={p.code}>{p.label}</option>
                      ))}
                    </select>
                    <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => addMethod(null)} data-testid="prod-form-add-method">
                      <Plus className="w-3 h-3 mr-1" /> Custom
                    </Button>
                  </div>
                </div>
                <div className="space-y-2 mt-3">
                  {form.printing_methods.length === 0 && (
                    <div className="text-xs text-neutral-500 italic">Sin métodos. Agrega DTF, Sublimación, etc.</div>
                  )}
                  {form.printing_methods.map((m, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-start bg-white p-2 border border-neutral-200">
                      <Input placeholder="Código" className="col-span-3 rounded-none h-9 text-sm font-mono" value={m.code} onChange={(e) => updMethod(i, "code", e.target.value.toLowerCase().replace(/\s+/g, "_"))} data-testid={`prod-method-code-${i}`} />
                      <Input placeholder="Etiqueta" className="col-span-3 rounded-none h-9 text-sm" value={m.label} onChange={(e) => updMethod(i, "label", e.target.value)} data-testid={`prod-method-label-${i}`} />
                      <Input placeholder="Descripción" className="col-span-4 rounded-none h-9 text-sm" value={m.description || ""} onChange={(e) => updMethod(i, "description", e.target.value)} />
                      <Input type="number" step="0.5" placeholder="±$" title="Diferencia sobre precio base (puede ser negativo)" className="col-span-1 rounded-none h-9 text-sm" value={m.extra_price} onChange={(e) => updMethod(i, "extra_price", e.target.value)} data-testid={`prod-method-price-${i}`} />
                      <button onClick={() => rmMethod(i)} className="p-2 hover:bg-neutral-100"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Design service */}
              <div className="border-2 border-purple-500/70 p-4 bg-purple-50/30">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={!!form.design_service?.enabled}
                    onChange={(e) => setForm({ ...form, design_service: { ...form.design_service, enabled: e.target.checked } })}
                    data-testid="prod-form-design-service-enabled"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">Servicio de diseño gráfico</div>
                    <div className="text-xs text-neutral-500">Ofrece la opción de que tu equipo diseñe por el cliente. Se cobra por unidad.</div>
                  </div>
                </label>
                {form.design_service?.enabled && (
                  <div className="mt-3 grid md:grid-cols-3 gap-2">
                    <div>
                      <Label className="text-xs font-mono uppercase">Precio (MXN)</Label>
                      <Input
                        type="number"
                        step="1"
                        className="rounded-none mt-1 h-9"
                        value={form.design_service?.price ?? 99}
                        onChange={(e) => setForm({ ...form, design_service: { ...form.design_service, price: e.target.value } })}
                        data-testid="prod-form-design-service-price"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs font-mono uppercase">Descripción</Label>
                      <Input
                        className="rounded-none mt-1 h-9"
                        value={form.design_service?.description ?? ""}
                        onChange={(e) => setForm({ ...form, design_service: { ...form.design_service, description: e.target.value } })}
                        data-testid="prod-form-design-service-desc"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Extra services — customizable list of additional add-ons per product */}
              <div className="border border-emerald-500/60 p-4 bg-emerald-50/30" data-testid="prod-form-extras-block">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="font-medium text-sm">Servicios adicionales</div>
                    <div className="text-xs text-neutral-600">Agrega servicios opt-in (envoltura, urgencia, instalación, etc.). Se cobran como cargo único por línea, no por unidad.</div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="rounded-none h-8"
                    onClick={() => setForm({
                      ...form,
                      extra_services: [
                        ...(form.extra_services || []),
                        { id: "", name: "", description: "", price: 0, enabled: true },
                      ],
                    })}
                    data-testid="prod-form-add-extra"
                  >
                    <Plus className="w-3 h-3 mr-1" /> Agregar servicio
                  </Button>
                </div>

                {(form.extra_services || []).length === 0 && (
                  <div className="text-xs text-neutral-500 italic mt-2">
                    Sin servicios adicionales. Ejemplos: envoltura de regalo ($50), producción urgente ($200), instalación ($150).
                  </div>
                )}

                <div className="space-y-2 mt-2">
                  {(form.extra_services || []).map((svc, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-start bg-white border border-neutral-200 p-2" data-testid={`prod-form-extra-${idx}`}>
                      <div className="col-span-4">
                        <Label className="text-[10px] font-mono uppercase text-neutral-500">Nombre</Label>
                        <Input
                          className="rounded-none mt-1 h-8 text-sm"
                          value={svc.name || ""}
                          onChange={(e) => {
                            const arr = [...form.extra_services];
                            arr[idx] = { ...svc, name: e.target.value };
                            setForm({ ...form, extra_services: arr });
                          }}
                          placeholder="Envoltura de regalo"
                          data-testid={`prod-form-extra-name-${idx}`}
                        />
                      </div>
                      <div className="col-span-5">
                        <Label className="text-[10px] font-mono uppercase text-neutral-500">Descripción (opcional)</Label>
                        <Input
                          className="rounded-none mt-1 h-8 text-sm"
                          value={svc.description || ""}
                          onChange={(e) => {
                            const arr = [...form.extra_services];
                            arr[idx] = { ...svc, description: e.target.value };
                            setForm({ ...form, extra_services: arr });
                          }}
                          placeholder="Caja + moño personalizado"
                          data-testid={`prod-form-extra-desc-${idx}`}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-[10px] font-mono uppercase text-neutral-500">Precio</Label>
                        <Input
                          type="number"
                          step="1"
                          min="0"
                          className="rounded-none mt-1 h-8 text-sm"
                          value={svc.price ?? 0}
                          onChange={(e) => {
                            const arr = [...form.extra_services];
                            arr[idx] = { ...svc, price: e.target.value };
                            setForm({ ...form, extra_services: arr });
                          }}
                          data-testid={`prod-form-extra-price-${idx}`}
                        />
                      </div>
                      <div className="col-span-1 flex flex-col items-center gap-1 pt-4">
                        <label className="flex items-center gap-1 cursor-pointer" title={svc.enabled === false ? "Activar" : "Desactivar (no se mostrará al cliente)"}>
                          <input
                            type="checkbox"
                            checked={svc.enabled !== false}
                            onChange={(e) => {
                              const arr = [...form.extra_services];
                              arr[idx] = { ...svc, enabled: e.target.checked };
                              setForm({ ...form, extra_services: arr });
                            }}
                            className="w-3.5 h-3.5 accent-emerald-600"
                            data-testid={`prod-form-extra-enabled-${idx}`}
                          />
                        </label>
                        <button
                          type="button"
                          onClick={() => {
                            const arr = form.extra_services.filter((_, i) => i !== idx);
                            setForm({ ...form, extra_services: arr });
                          }}
                          className="p-1 text-neutral-400 hover:text-red-600"
                          data-testid={`prod-form-extra-remove-${idx}`}
                          title="Eliminar servicio"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Mockup config */}
              <div className="border border-neutral-200 p-4">
                <div className="font-medium text-sm mb-3">Zona de impresión (mockup por defecto)</div>
                <div className="grid grid-cols-4 gap-2">
                  {["top", "left", "width", "height"].map((k) => (
                    <div key={k}>
                      <Label className="text-xs font-mono uppercase">{k}%</Label>
                      <Input type="number" min="0" max="100" step="1" className="rounded-none mt-1 h-9" value={form.mockup[k]} onChange={(e) => setForm({ ...form, mockup: { ...form.mockup, [k]: Number(e.target.value) } })} />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-neutral-500 mt-2">El cliente podrá reposicionar y redimensionar el diseño en el mockup interactivo.</p>
              </div>

              <div className="grid md:grid-cols-2 gap-3 items-end pt-2 border-t border-neutral-100">
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
                  Producto activo (visible en tienda)
                </label>
                <div>
                  <Label className="text-xs font-mono uppercase">Orden en la tienda</Label>
                  <Input
                    type="number"
                    className="rounded-none mt-1 h-9"
                    value={form.sort_order || 0}
                    onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
                    data-testid="prod-form-sort-order"
                  />
                  <p className="text-[10px] text-neutral-500 mt-1 font-mono">Menor = aparece primero. Usa números negativos para forzar arriba de todo.</p>
                </div>
              </div>

              <Button onClick={save} disabled={saving} className="w-full brutal-btn bg-black hover:bg-[#FF3B30] text-white h-11" data-testid="prod-form-save">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Guardar producto
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
